import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useErrorHandler } from "../hooks/useErrorHandler";
import {
  Add as AddIcon,
  Receipt,
  Person,
  CalendarToday,
  Description,
  CheckCircle,
  Cancel,
  Schedule,
  Close,
  Edit,
  AttachMoney,
  CloudUpload,
  Delete,
  Visibility,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { expenseService, fileService } from "../services";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";

const validationSchema = Yup.object({
  title: Yup.string()
    .min(3, "Title must be at least 3 characters")
    .required("Title is required"),
  amount: Yup.number()
    .positive("Amount must be positive")
    .required("Amount is required"),
  currency: Yup.string()
    .oneOf(
      ["INR", "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "CNY", "SGD"],
      "Invalid currency"
    )
    .required("Currency is required"),
  category: Yup.string()
    .oneOf(
      ["software", "hardware", "subscription", "travel", "training", "other"],
      "Invalid category"
    )
    .required("Category is required"),
  expense_date: Yup.date()
    .max(new Date(), "Expense date cannot be in the future")
    .required("Expense date is required"),
  description: Yup.string()
    .min(10, "Description must be at least 10 characters")
    .required("Description is required"),
});

const getStatusIcon = (status) => {
  switch (status) {
    case "approved":
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    case "rejected":
      return <Cancel className="w-5 h-5 text-red-500" />;
    case "pending":
      return <Schedule className="w-5 h-5 text-yellow-500" />;
    default:
      return <Schedule className="w-5 h-5 text-gray-500" />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "approved":
      return "bg-green-100 text-green-700 border-green-200";
    case "rejected":
      return "bg-red-100 text-red-700 border-red-200";
    case "pending":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const getCategoryLabel = (category) => {
  const labels = {
    software: "Software",
    hardware: "Hardware",
    subscription: "Subscription",
    travel: "Travel",
    training: "Training",
    other: "Other",
  };
  return labels[category] || category;
};

const getCurrencySymbol = (currency) => {
  const symbols = {
    INR: "₹",
    USD: "$",
    EUR: "€",
    GBP: "£",
    JPY: "¥",
    AUD: "A$",
    CAD: "C$",
    CHF: "CHF",
    CNY: "¥",
    SGD: "S$",
  };
  return symbols[currency] || currency;
};

export default function Expenses() {
  const { user } = useAuth();
  const { showError, reset: resetError } = useErrorHandler();
  // Check if user is EMPLOYEE (handle both old and new role formats)
  const userRole = user?.role?.toUpperCase() || user?.role;
  const isEmployee = userRole === "EMPLOYEE" || user?.role === "employee";
  const [open, setOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const expensesRef = useRef([]); // Ref to track expenses for error handling
  const [filters, setFilters] = useState({
    status: isEmployee ? "all" : "pending",
    employeeId: "",
  });

  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  const formik = useFormik({
    initialValues: {
      title: "",
      amount: "",
      currency: "INR",
      category: "other",
      expense_date: "",
      description: "",
      receipt_url: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        let receiptUrl = values.receipt_url;

        // Upload receipt file if a new file is selected
        if (receiptFile) {
          setUploadingReceipt(true);
          try {
            const formData = new FormData();
            formData.append("receipt", receiptFile);

            console.log("Uploading receipt:", {
              fileName: receiptFile.name,
              fileSize: receiptFile.size,
              fileType: receiptFile.type,
            });

            const uploadResponse = await expenseService.uploadReceipt(formData);

            if (!uploadResponse?.data?.receipt_url) {
              throw new Error("No receipt URL returned from server");
            }

            receiptUrl = uploadResponse.data.receipt_url;
            console.log("Receipt uploaded successfully:", receiptUrl);
          } catch (uploadErr) {
            console.error("Receipt upload error:", uploadErr);
            const errorMessage =
              uploadErr.response?.data?.error ||
              uploadErr.response?.data?.message ||
              uploadErr.message ||
              "Failed to upload receipt. Please try again.";
            toast.error(errorMessage);
            setUploadingReceipt(false);
            setSubmitting(false);
            return;
          } finally {
            setUploadingReceipt(false);
          }
        }

        const payload = {
          title: values.title,
          amount: values.amount,
          currency: values.currency,
          category: values.category,
          expense_date: values.expense_date,
          description: values.description,
          receipt_url: receiptUrl || null,
        };

        if (editingExpense) {
          await expenseService.update(editingExpense._id, payload);
          toast.success("Expense request updated successfully!");
        } else {
          await expenseService.request(payload);
          toast.success("Expense request submitted successfully!");
        }

        // Close modal and reset form first
        resetForm();
        setReceiptFile(null);
        setReceiptPreview(null);
        setOpen(false);
        setEditingExpense(null);

        // Refresh expenses list - use setTimeout to ensure state updates complete first
        setTimeout(() => {
          fetchExpenses().catch((refreshErr) => {
            console.error(
              "Failed to refresh expenses after submission:",
              refreshErr
            );
            // Don't show error toast here as submission was successful
          });
        }, 100);
      } catch (err) {
        const message =
          err.response?.data?.error ||
          err.response?.data?.message ||
          (editingExpense
            ? "Failed to update expense request"
            : "Failed to submit expense request");
        toast.error(message);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a PDF or image file (JPEG, PNG, GIF, WebP)");
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }

      setReceiptFile(file);
      formik.setFieldValue("receipt_url", ""); // Clear URL if file is uploaded

      // Create preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setReceiptPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview(null);
      }
    }
  };

  const handleRemoveReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview(null);
    formik.setFieldValue("receipt_url", "");
  };

  const fetchExpenses = useCallback(async () => {
    if (!user) return;

    resetError(); // Reset error handler for new fetch attempt

    try {
      setLoading(true);
      const endpoint = isEmployee
        ? expenseService.getMyRequests
        : expenseService.getPending;
      const params = {
        status: filters.status,
      };

      if (!isEmployee && filters.employeeId) {
        params.employeeId = filters.employeeId;
      }

      const response = await endpoint(params);

      console.log("Expenses API response:", response);

      // The API interceptor extracts data from { success: true, data: [...] }
      // So response.data should be the array directly after interceptor processing
      let expensesData = [];

      if (response && response.data !== undefined) {
        if (Array.isArray(response.data)) {
          expensesData = response.data;
        } else if (
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data)
        ) {
          // Fallback: if data is still wrapped (interceptor didn't process it)
          expensesData = response.data.data;
        } else if (
          response.data &&
          typeof response.data === "object" &&
          !response.data.error
        ) {
          // If it's a single expense object, wrap it in array
          expensesData = [response.data];
        }
      }

      console.log("Parsed expenses data:", expensesData.length, "expenses");
      setExpenses(expensesData);
      expensesRef.current = expensesData; // Update ref with new data
    } catch (err) {
      console.error("Failed to fetch expenses", err);
      console.error("Error details:", {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        url: err.config?.url,
        hasExistingExpenses: expensesRef.current.length > 0,
      });

      // Check if we have existing expenses using ref (always current)
      const hasExistingData = expensesRef.current.length > 0;

      // Only show error if we don't have existing expenses to display
      // This way, if there's a network error but we have cached data, we keep showing it
      if (!hasExistingData) {
        showError(err, "Failed to load expense records");
        // Only clear if this is the first load and we have no data
        setExpenses([]);
        expensesRef.current = [];
      } else {
        // If we have existing expenses, just log the error but don't clear the list
        console.warn("Failed to refresh expenses, but keeping existing data");
        toast.warn("Could not refresh expense list. Showing cached data.", {
          autoClose: 3000,
        });
        // Keep existing expenses - don't clear them (already in state)
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filters.status, filters.employeeId]);

  useEffect(() => {
    if (user) {
      setFilters((prev) => {
        const newStatus = isEmployee ? "all" : "pending";
        // Only update if status actually changed to prevent unnecessary re-renders
        if (prev.status !== newStatus || prev.employeeId !== "") {
          return {
            status: newStatus,
            employeeId: "",
          };
        }
        return prev;
      });
    }
  }, [user, isEmployee]);

  useEffect(() => {
    if (user) {
      fetchExpenses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filters.status, filters.employeeId]);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const response = await api.get("/employees");
        const payload = response.data;
        const employeeData = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];
        setEmployees(employeeData);
      } catch (err) {
        console.error("Failed to load employees list", err);
      }
    };

    if (user && !isEmployee) {
      loadEmployees();
    }
  }, [user]);

  const handleFilterChange = (field) => (event) => {
    const { value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleResetFilters = () => {
    if (!user) return;
    setFilters({
      status: isEmployee ? "all" : "pending",
      employeeId: "",
    });
  };

  const handleApprove = async (expenseId) => {
    try {
      await expenseService.approve(expenseId);
      toast.success("Expense approved successfully!");
      fetchExpenses();
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to approve expense";
      toast.error(message);
    }
  };

  const handleReject = async (expenseId) => {
    const reason = prompt("Please provide a reason for rejection:");
    if (reason === null) return; // User cancelled
    if (!reason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    try {
      await expenseService.reject(expenseId, reason);
      toast.success("Expense rejected successfully!");
      fetchExpenses();
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to reject expense";
      toast.error(message);
    }
  };

  const isEmployeeUser = isEmployee;
  const statusOptions = [
    { label: "All Statuses", value: "all" },
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
  ];

  const categoryOptions = [
    { label: "Software", value: "software" },
    { label: "Hardware", value: "hardware" },
    { label: "Subscription", value: "subscription" },
    { label: "Travel", value: "travel" },
    { label: "Training", value: "training" },
    { label: "Other", value: "other" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8 flex justify-between items-center flex-wrap gap-4"
      >
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2">
            Expense Management
          </h1>
          <p className="text-gray-600">
            {isEmployeeUser
              ? "Submit and track your expense reimbursement requests"
              : "Review and approve employee expense requests"}
          </p>
        </div>
        {isEmployeeUser && (
          <motion.button
            onClick={() => setOpen(true)}
            className="btn-primary flex items-center gap-2 px-6 py-3"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <AddIcon />
            <span>Submit Expense</span>
          </motion.button>
        )}
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card mb-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={handleFilterChange("status")}
              className="input-field"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {!isEmployeeUser && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Employee
              </label>
              <select
                value={filters.employeeId}
                onChange={handleFilterChange("employeeId")}
                className="input-field"
              >
                <option value="">All Employees</option>
                {employees.map((employee) => (
                  <option key={employee._id} value={employee._id}>
                    {employee.user_id?.name || "Unknown"}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={handleResetFilters}>
            Clear Filters
          </Button>
        </div>
      </motion.div>

      {/* Expenses Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="w-16 h-16 skeleton rounded-2xl"></div>
                <div className="w-8 h-8 skeleton rounded-xl"></div>
              </div>
              <div className="h-6 skeleton rounded mb-2 w-3/4"></div>
              <div className="h-4 skeleton rounded w-full mb-2"></div>
              <div className="h-4 skeleton rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : expenses.length === 0 ? (
        <div className="card text-center py-12">
          <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No expense requests found</p>
        </div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {expenses.map((expense, index) => {
            const categoryColors = {
              software: "bg-purple-100 text-purple-700 border-purple-200",
              hardware: "bg-blue-100 text-blue-700 border-blue-200",
              subscription: "bg-indigo-100 text-indigo-700 border-indigo-200",
              travel: "bg-green-100 text-green-700 border-green-200",
              training: "bg-orange-100 text-orange-700 border-orange-200",
              other: "bg-gray-100 text-gray-700 border-gray-200",
            };

            return (
              <motion.div
                key={expense._id || expense.id}
                variants={cardVariants}
                className="card group overflow-hidden hover:shadow-xl transition-all duration-300"
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
              >
                {/* Header Section */}
                <div className="relative bg-gradient-to-r from-primary/5 via-primary/3 to-transparent p-6 -m-6 mb-4 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-white shadow-md flex-shrink-0">
                        <Receipt className="w-7 h-7" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-1">
                          {expense.title}
                        </h3>
                        {!isEmployeeUser && (
                          <p className="text-sm text-gray-600 flex items-center gap-1.5">
                            <Person className="w-4 h-4 text-gray-400" />
                            <span className="truncate">
                              {expense.employee_id?.user_id?.name ||
                                "Unknown Employee"}
                            </span>
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span
                            className={`px-3 py-1 rounded-lg text-xs font-semibold border ${
                              categoryColors[expense.category] ||
                              categoryColors.other
                            }`}
                          >
                            {getCategoryLabel(expense.category)}
                          </span>
                          <div
                            className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 ${getStatusColor(
                              expense.status
                            )}`}
                          >
                            {getStatusIcon(expense.status)}
                            <span className="capitalize font-medium">
                              {expense.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {isEmployeeUser && expense.status === "pending" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingExpense(expense);
                          setReceiptFile(null);
                          setReceiptPreview(null);
                          formik.setValues({
                            title: expense.title || "",
                            amount: expense.amount || "",
                            currency: expense.currency || "INR",
                            category: expense.category || "other",
                            expense_date: expense.expense_date
                              ? new Date(expense.expense_date)
                                  .toISOString()
                                  .split("T")[0]
                              : "",
                            description: expense.description || "",
                            receipt_url: expense.receipt_url || "",
                          });
                          setOpen(true);
                        }}
                        className="p-2 hover:bg-white/80 rounded-lg transition-colors flex-shrink-0"
                        title="Edit expense request"
                      >
                        <Edit className="w-4 h-4 text-gray-600" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Content Section */}
                <div className="space-y-4">
                  {/* Amount - Prominent Display */}
                  <div className="bg-gradient-to-r from-primary/5 to-transparent rounded-xl p-4 border border-primary/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AttachMoney className="w-5 h-5 text-primary" />
                        <span className="text-sm font-semibold text-gray-700">
                          Amount
                        </span>
                      </div>
                      <span className="text-2xl font-bold text-primary">
                        {getCurrencySymbol(expense.currency || "INR")}
                        {expense.amount?.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }) || "0.00"}
                      </span>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CalendarToday className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block">
                          Expense Date
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {expense.expense_date
                            ? new Date(expense.expense_date).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                }
                              )
                            : "--"}
                        </span>
                      </div>
                    </div>

                    {expense.description && (
                      <div className="flex items-start gap-3 text-sm pt-2 border-t border-gray-100">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Description className="w-4 h-4 text-gray-600" />
                        </div>
                        <div className="flex-1">
                          <span className="text-xs text-gray-500 block mb-1">
                            Description
                          </span>
                          <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">
                            {expense.description}
                          </p>
                        </div>
                      </div>
                    )}

                    {expense.receipt_url && (
                      <div className="pt-2 border-t border-gray-100">
                        <button
                          type="button"
                          onClick={() => fileService.openDownload(expense.receipt_url, (err) => toast.error(err))}
                          className="group w-full flex items-center gap-3 p-3 bg-gradient-to-r from-primary/5 to-primary/10 hover:from-primary/10 hover:to-primary/15 rounded-xl border border-primary/20 hover:border-primary/30 transition-all duration-200 hover:shadow-md text-left"
                        >
                          <div className="w-10 h-10 bg-primary/10 group-hover:bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                            <Receipt className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-0.5">
                              Receipt
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-primary group-hover:text-primary/80">
                                View Receipt
                              </span>
                              <Visibility className="w-4 h-4 text-primary/60 group-hover:text-primary group-hover:scale-110 transition-transform" />
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <svg
                              className="w-5 h-5 text-primary/60 group-hover:text-primary group-hover:translate-x-1 transition-transform"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          </div>
                        </button>
                      </div>
                    )}

                    {expense.rejection_reason && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                        <div className="flex items-start gap-2">
                          <Cancel className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="text-xs font-semibold text-red-700 block mb-1">
                              Rejection Reason
                            </span>
                            <p className="text-sm text-red-600">
                              {expense.rejection_reason}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons for Admin/HR */}
                  {!isEmployeeUser && expense.status === "pending" && (
                    <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                      <Button
                        variant="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(expense._id);
                        }}
                        className="flex-1"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(expense._id);
                        }}
                        className="flex-1"
                      >
                        <Cancel className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Submit Expense Modal */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setOpen(false);
                formik.resetForm();
                setEditingExpense(null);
              }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-3xl shadow-soft-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <h2 className="text-2xl font-bold text-primary">
                    {editingExpense ? "Edit Expense Request" : "Submit Expense"}
                  </h2>
                  <button
                    onClick={() => {
                      setOpen(false);
                      formik.resetForm();
                      setEditingExpense(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-2xl transition-colors"
                  >
                    <Close className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={formik.handleSubmit} className="p-6">
                  <div className="space-y-4">
                    {/* Title */}
                    <Input
                      label="Title"
                      id="title"
                      name="title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.title && formik.errors.title
                          ? formik.errors.title
                          : undefined
                      }
                      placeholder="e.g., Premium AI Subscription"
                    />

                    {/* Amount and Currency */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-2">
                        <Input
                          type="number"
                          label="Amount"
                          id="amount"
                          name="amount"
                          value={formik.values.amount}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.amount && formik.errors.amount
                              ? formik.errors.amount
                              : undefined
                          }
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Currency
                        </label>
                        <select
                          id="currency"
                          name="currency"
                          value={formik.values.currency}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`input-field ${
                            formik.touched.currency && formik.errors.currency
                              ? "border-red-500 focus:ring-red-500/20"
                              : ""
                          }`}
                        >
                          <option value="INR">INR (₹)</option>
                          <option value="USD">USD ($)</option>
                          <option value="EUR">EUR (€)</option>
                          <option value="GBP">GBP (£)</option>
                          <option value="JPY">JPY (¥)</option>
                          <option value="AUD">AUD (A$)</option>
                          <option value="CAD">CAD (C$)</option>
                          <option value="CHF">CHF</option>
                          <option value="CNY">CNY (¥)</option>
                          <option value="SGD">SGD (S$)</option>
                        </select>
                        {formik.touched.currency && formik.errors.currency && (
                          <p className="mt-2 text-sm text-red-600">
                            {formik.errors.currency}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        id="category"
                        name="category"
                        value={formik.values.category}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`input-field ${
                          formik.touched.category && formik.errors.category
                            ? "border-red-500 focus:ring-red-500/20"
                            : ""
                        }`}
                      >
                        {categoryOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {formik.touched.category && formik.errors.category && (
                        <p className="mt-2 text-sm text-red-600">
                          {formik.errors.category}
                        </p>
                      )}
                    </div>

                    {/* Expense Date */}
                    <Input
                      type="date"
                      label="Expense Date"
                      id="expense_date"
                      name="expense_date"
                      value={formik.values.expense_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.expense_date &&
                        formik.errors.expense_date
                          ? formik.errors.expense_date
                          : undefined
                      }
                    />

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        rows={4}
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`input-field resize-none ${
                          formik.touched.description &&
                          formik.errors.description
                            ? "border-red-500 focus:ring-red-500/20"
                            : ""
                        }`}
                        placeholder="Describe the expense and its business purpose..."
                      />
                      {formik.touched.description &&
                        formik.errors.description && (
                          <p className="mt-2 text-sm text-red-600">
                            {formik.errors.description}
                          </p>
                        )}
                    </div>

                    {/* Receipt Upload */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Receipt (PDF or Image) - Optional
                      </label>
                      {!receiptFile && !formik.values.receipt_url && (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors">
                          <input
                            type="file"
                            id="receipt"
                            name="receipt"
                            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                          <label
                            htmlFor="receipt"
                            className="cursor-pointer flex flex-col items-center gap-2"
                          >
                            <CloudUpload className="w-12 h-12 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              Click to upload receipt (PDF or Image)
                            </span>
                            <span className="text-xs text-gray-500">
                              Max size: 10MB
                            </span>
                          </label>
                        </div>
                      )}
                      {receiptFile && (
                        <div className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Receipt className="w-8 h-8 text-primary" />
                              <div>
                                <p className="text-sm font-medium text-gray-700">
                                  {receiptFile.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {(receiptFile.size / 1024).toFixed(2)} KB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={handleRemoveReceipt}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Delete className="w-5 h-5 text-red-500" />
                            </button>
                          </div>
                          {receiptPreview && (
                            <div className="mt-4">
                              <img
                                src={receiptPreview}
                                alt="Receipt preview"
                                className="max-w-full h-auto max-h-48 rounded-lg border border-gray-200"
                              />
                            </div>
                          )}
                        </div>
                      )}
                      {formik.values.receipt_url && !receiptFile && (
                        <div className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Receipt className="w-8 h-8 text-primary" />
                              <div>
                                <p className="text-sm font-medium text-gray-700">
                                  Receipt uploaded
                                </p>
                                <button
                                  type="button"
                                  onClick={() => fileService.openDownload(formik.values.receipt_url, (err) => toast.error(err))}
                                  className="text-xs text-primary hover:underline flex items-center gap-1"
                                >
                                  <Visibility className="w-4 h-4" />
                                  View receipt
                                </button>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={handleRemoveReceipt}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Delete className="w-5 h-5 text-red-500" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setOpen(false);
                        formik.resetForm();
                        setReceiptFile(null);
                        setReceiptPreview(null);
                        setEditingExpense(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      loading={formik.isSubmitting || uploadingReceipt}
                      disabled={formik.isSubmitting || uploadingReceipt}
                    >
                      {uploadingReceipt
                        ? "Uploading Receipt..."
                        : formik.isSubmitting
                        ? editingExpense
                          ? "Updating..."
                          : "Submitting..."
                        : editingExpense
                        ? "Update Request"
                        : "Submit Request"}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
